'use client';

import { useBunja } from 'bunja/react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { useRouter } from '@flipster/i18n/routing';
import { SpecialButton } from '@flipster/react/design-system/special/button';
import { css } from '@flipster/styled-system/css';
import { center, vstack } from '@flipster/styled-system/patterns';
import segment from '@flipster/utils/segment';
import { CompetitionsEvent } from '@flipster/utils/segment/events/competitions';

import { registerCompetitionBunja } from '../../event/competition/action';
import { CompetitionIdContext } from '../../event/competition/state';
import { useAuth } from '../../shared/auth/state';
import { Dice } from './dice';
import { GamePiece } from './game-piece';
import { RewardBox } from './reward-box';
import { rollAndMoveBunja } from './state';

// Board layout: 7x7 outer ring with 24 cells (positions 0-23)
// Clockwise direction, starting from bottom-left
// Layout pattern:
//  6  7  8  9 10 11 12
//  5                 13
//  4                 14
//  3                 15
//  2                 16
//  1                 17
//  0 23 22 21 20 19 18
const TOTAL_CELLS = 24;
const BOARD_MAX_WIDTH = 656;

// Board image has padding (decorative corners), cells are inside
// Full image: 656px, inner board: 616px, padding each side: 20px (≈3.05%)
const BOARD_PADDING_PERCENT = (20 / 656) * 100;
const BOARD_INNER_PERCENT = 100 - BOARD_PADDING_PERCENT * 2;

const CELL_PERCENT = BOARD_INNER_PERCENT / 7;

// Calculate cell positions on the board using percentages (24 cells in 7x7 outer ring)
// Clockwise from bottom-left corner
// Returns { x, y } as percentage values (0-100), accounting for board padding
const getCellPosition = (index: number): { x: number; y: number } => {
  const normalizedIndex = index % TOTAL_CELLS;

  const withPadding = (col: number, row: number) => ({
    x: BOARD_PADDING_PERCENT + col * CELL_PERCENT,
    y: BOARD_PADDING_PERCENT + row * CELL_PERCENT,
  });

  // Left column (0-6): 7 cells, bottom to top
  if (normalizedIndex <= 6) {
    return withPadding(0, 6 - normalizedIndex);
  }

  // Top row (7-12): 6 cells, left to right (excluding top-left corner)
  if (normalizedIndex <= 12) {
    const colIndex = normalizedIndex - 6;
    return withPadding(colIndex, 0);
  }

  // Right column (13-18): 6 cells, top to bottom (excluding top-right corner)
  if (normalizedIndex <= 18) {
    const rowIndex = normalizedIndex - 12;
    return withPadding(6, rowIndex);
  }

  // Bottom row (19-23): 5 cells, right to left (excluding bottom-right and bottom-left corners)
  const colIndex = 6 - (normalizedIndex - 18);
  return withPadding(colIndex, 6);
};

export const GameBoard = () => {
  const {
    rollAndDiceSectionLayout,
    currentGameAtom,
    isRollingAtom,
    lastRollResultAtom,
    rollDiceMutationAtom,
    invalidRollAndMoveAtom,
    showRewardAtom,
    snapshotRewardsAtom,
    unfreezeRewardsAtom,
  } = useBunja(rollAndMoveBunja);
  const { registerCompetition } = useBunja(registerCompetitionBunja);
  const { isCookieLoggedIn } = useAuth();
  const router = useRouter();
  const competitionId = useContext(CompetitionIdContext);

  const currentGame = useAtomValue(currentGameAtom);
  const [isRolling, setIsRolling] = useAtom(isRollingAtom);
  const lastRollResult = useAtomValue(lastRollResultAtom);
  const [{ mutateAsync: rollDice, isPending: isRollPending }] = useAtom(rollDiceMutationAtom);
  const invalidRollAndMove = useSetAtom(invalidRollAndMoveAtom);
  const snapshotRewards = useSetAtom(snapshotRewardsAtom);
  const unfreezeRewards = useSetAtom(unfreezeRewardsAtom);

  const [diceValue, setDiceValue] = useState<number | null>(null);
  const [animatingPosition, setAnimatingPosition] = useState<number | null>(null);
  const [showReward, setShowReward] = useAtom(showRewardAtom);

  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const boardSkin = rollAndDiceSectionLayout?.boardSkin?.url;
  const remainingRolls = currentGame?.remainingRolls ?? 0;

  const isAnimating = animatingPosition !== null;
  const piecePosition = animatingPosition ?? currentGame?.currentPosition ?? 0;

  const delay = (ms: number) =>
    new Promise((resolve) => {
      animationTimeoutRef.current = setTimeout(resolve, ms);
    });

  const handleRegister = useCallback(async () => {
    segment.track(CompetitionsEvent.CompetitionTradingRegisterClick, {
      competition_id: competitionId,
      button_location: 'event_tab_lucky_roll',
    });

    if (!isCookieLoggedIn) {
      router.push('/login');
      return;
    }

    await registerCompetition();
    invalidRollAndMove();
  }, [competitionId, isCookieLoggedIn, registerCompetition, invalidRollAndMove, router]);

  const handleRoll = useCallback(async () => {
    if (!currentGame || isRollPending || isRolling || isAnimating || remainingRolls <= 0) return;

    snapshotRewards();
    setIsRolling(true);
    setDiceValue(null);

    let result;
    try {
      result = await rollDice();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('rollDice error:', error);
      setIsRolling(false);
      return;
    }

    if (!result) {
      setIsRolling(false);
      return;
    }

    const { roll, bonusMove, positionFrom, positionTo, prizeId, isGoal } = result;

    segment.track(CompetitionsEvent.CompetitionLuckyRollDiceClick, {
      roll_result: roll,
      bonus_move: bonusMove,
      position_from: positionFrom,
      position_to: positionTo,
      remaining_rolls: remainingRolls - 1,
      prize_id: prizeId,
      is_goal: isGoal,
    });

    setIsRolling(false);
    setDiceValue(roll);

    let currentPos = positionFrom;
    setAnimatingPosition(currentPos);

    const diceSteps = roll;
    for (let i = 0; i < diceSteps; i++) {
      await delay(300);
      currentPos = (currentPos + 1) % TOTAL_CELLS;
      setAnimatingPosition(currentPos);

      if (isGoal && currentPos === 0) break;
    }

    if (bonusMove !== 0 && !(isGoal && currentPos === 0)) {
      await delay(500);
      const bonusSteps = Math.abs(bonusMove);
      const direction = bonusMove > 0 ? 1 : -1;

      for (let i = 0; i < bonusSteps; i++) {
        await delay(300);
        currentPos = (currentPos + direction + TOTAL_CELLS) % TOTAL_CELLS;
        setAnimatingPosition(currentPos);

        if (isGoal && currentPos === 0) break;
      }
    }

    await delay(500);

    setAnimatingPosition(null);
    setIsRolling(false);

    if (prizeId) {
      setShowReward(true);
    }

    invalidRollAndMove();
  }, [
    currentGame,
    isRollPending,
    isRolling,
    setShowReward,
    isAnimating,
    remainingRolls,
    rollDice,
    setIsRolling,
    invalidRollAndMove,
    snapshotRewards,
  ]);

  useEffect(() => {
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, []);

  const handleRewardClose = useCallback(() => {
    setShowReward(false);
    unfreezeRewards();
  }, [setShowReward, unfreezeRewards]);

  const isUpcoming = currentGame?.boardGameStatus === 'UPCOMING';
  const isFinished = currentGame?.boardGameStatus === 'FINISHED';
  const isRegistered = currentGame?.isRegistered ?? false;
  const showButton = !isFinished;

  const isRollDisabled = isRollPending || isRolling || isAnimating || showReward || remainingRolls <= 0;

  return (
    <div className={vstack({ gap: 24, w: '100%', maxW: 656, mx: 'auto' })}>
      <div
        className={css({
          position: 'relative',
          w: '100%',
          maxW: BOARD_MAX_WIDTH,
          aspectRatio: '1 / 1',
          mx: 'auto',
        })}
      >
        {boardSkin && (
          <img
            src={boardSkin}
            alt='game board'
            className={css({
              position: 'absolute',
              inset: 0,
              w: '100%',
              h: '100%',
              objectFit: 'cover',
              borderRadius: 16,
            })}
          />
        )}

        <div
          className={center({
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            w: isUpcoming ? 'auto' : '12.2%',
            aspectRatio: isUpcoming ? 'auto' : '1 / 1',
          })}
        >
          {isUpcoming ? (
            <span className={css({ textStyle: 'Text/L/Strong', color: 'content.default.level-5' })}>
              <FormattedMessage id='Campaign.r260204.Starting_soon' />
            </span>
          ) : (
            <Dice value={diceValue} isRolling={isRolling} />
          )}
        </div>

        {showReward && lastRollResult?.prizeId && (
          <RewardBox
            prizeId={lastRollResult.prizeId}
            isGoal={lastRollResult.isGoal}
            onClose={handleRewardClose}
            skin={
              lastRollResult.isGoal
                ? rollAndDiceSectionLayout?.goalRewardBoxSkin?.url
                : rollAndDiceSectionLayout?.rewardBoxSkin?.url
            }
          />
        )}
        {!isUpcoming && (
          <GamePiece
            position={piecePosition}
            getCellPosition={getCellPosition}
            cellPercent={CELL_PERCENT}
            skin={rollAndDiceSectionLayout?.pieceSkin?.url}
          />
        )}
      </div>

      {showButton &&
        (isUpcoming && isRegistered ? (
          <SpecialButton
            label={<FormattedMessage id='Common.r251028.Registered' />}
            onClick={() => {}}
            disabled
            css={{ w: '100%', maxW: 320 }}
          />
        ) : isRegistered ? (
          <>
            <SpecialButton
              label={<FormattedMessage id='Campaign.r260206.Roll_the_dice' />}
              onClick={handleRoll}
              disabled={isRollDisabled}
              css={{ w: '100%', maxW: 320 }}
            />
            <div
              className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-3', textAlign: 'center' })}
            >
              <FormattedMessage id='Campaign.r260204.Remaining_number' values={{ number: remainingRolls }} />
            </div>
          </>
        ) : (
          <SpecialButton
            label={<FormattedMessage id='Autogen_common.r251126.s_Register_Now' />}
            onClick={handleRegister}
            css={{ w: '100%', maxW: 320 }}
          />
        ))}
    </div>
  );
};
